import axios from "axios";
import moment from "moment";
import { authHeader } from "../_helpers/auth-header";

const API_BASE = "/api/v1/kpi";

/**
 * เลือกรอบการประเมินที่อยู่ในช่วงปีปัจจุบัน/วันปัจจุบันอัตโนมัติ (Dynamic Period Selector)
 */
export const findCurrentPeriod = periodList => {
  if (!Array.isArray(periodList) || periodList.length === 0) return null;

  const now = moment();

  // 1. ตรวจสอบรอบที่วันปัจจุบันอยู่ในช่วง StartDate ถึง EndDate
  const currentByDate = periodList.find(p => {
    if (!p || !p.startDate || !p.endDate) return false;
    const start = moment(p.startDate);
    const end = moment(p.endDate);
    return now.isSameOrAfter(start, "day") && now.isSameOrBefore(end, "day");
  });
  if (currentByDate) return currentByDate;

  // 2. ตรวจสอบตามปีงบประมาณไทยปัจจุบัน (1 ต.ค. - 30 ก.ย.)
  const currentMonth = now.month() + 1; // 1 - 12
  const currentYearAD = now.year();
  const currentFiscalYear = String(
    currentMonth >= 10 ? currentYearAD + 543 + 1 : currentYearAD + 543
  );

  const currentByFiscalYear =
    periodList.find(
      p =>
        p &&
        String(p.fiscalYear) === currentFiscalYear &&
        (p.isActive || !p.isClosed)
    ) ||
    periodList.find(
      p => p && String(p.fiscalYear) === currentFiscalYear
    );
  if (currentByFiscalYear) return currentByFiscalYear;

  // 3. ตรวจสอบรอบที่ตั้งสถานะเป็น Active
  const activePeriod = periodList.find(p => p && (p.isActive || p.isCurrent));
  if (activePeriod) return activePeriod;

  // 4. รายการแรกใน List (ซึ่ง Backend เรียงตามวันที่และปีงบประมาณล่าสุดไว้แล้ว)
  return periodList[0];
};

export const KpiService = {
  findCurrentPeriod,
  // 1. จัดการรอบการประเมิน (Periods)
  getAllPeriods: () => {
    return axios.get(`${API_BASE}/periods`, { headers: authHeader() });
  },
  getPeriodById: periodId => {
    return axios.get(`${API_BASE}/periods/${periodId}`, {
      headers: authHeader()
    });
  },
  createPeriod: data => {
    return axios.post(`${API_BASE}/periods`, data, { headers: authHeader() });
  },
  updatePeriod: (periodId, data) => {
    return axios.put(`${API_BASE}/periods/${periodId}`, data, {
      headers: authHeader()
    });
  },
  deletePeriod: periodId => {
    return axios.delete(`${API_BASE}/periods/${periodId}`, {
      headers: authHeader()
    });
  },
  clonePeriod: data => {
    return axios.post(`${API_BASE}/periods/clone`, data, {
      headers: authHeader()
    });
  },

  // 2. จัดการตัวชี้วัด (KPI Items)
  getItemsByPeriod: periodId => {
    return axios.get(`${API_BASE}/periods/${periodId}/items`, {
      headers: authHeader()
    });
  },
  getItemById: itemId => {
    return axios.get(`${API_BASE}/items/${itemId}`, { headers: authHeader() });
  },
  createItem: data => {
    return axios.post(`${API_BASE}/items`, data, { headers: authHeader() });
  },
  updateItem: (itemId, data) => {
    return axios.put(`${API_BASE}/items/${itemId}`, data, {
      headers: authHeader()
    });
  },
  deleteItem: itemId => {
    return axios.delete(`${API_BASE}/items/${itemId}`, {
      headers: authHeader()
    });
  },
  defendItem: (itemId, data) => {
    return axios.put(`${API_BASE}/items/${itemId}/defend`, data, {
      headers: authHeader()
    });
  },
  reviewItem: (itemId, data) => {
    return axios.put(`${API_BASE}/items/${itemId}/review`, data, {
      headers: authHeader()
    });
  },
  assignUsers: (itemId, data) => {
    return axios.post(`${API_BASE}/items/${itemId}/assign`, data, {
      headers: authHeader()
    });
  },
  getItemAuditLogs: itemId => {
    return axios.get(`${API_BASE}/items/${itemId}/audit-logs`, {
      headers: authHeader()
    });
  },

  // 3. KPI ของฉัน & การประเมินตนเอง (My KPIs & Self Assessment)
  getMyAssignments: (periodId, userName) => {
    return axios.get(`${API_BASE}/my-assignments`, {
      params: { periodId, userName },
      headers: authHeader()
    });
  },
  updateAssignmentMetadata: (assignmentId, data) => {
    return axios.put(`${API_BASE}/assignments/${assignmentId}/metadata`, data, {
      headers: authHeader()
    });
  },
  respondAgreement: (assignmentId, data) => {
    return axios.put(
      `${API_BASE}/assignments/${assignmentId}/agreement`,
      data,
      {
        headers: authHeader()
      }
    );
  },
  submitSelfAssessment: (assignmentId, data) => {
    return axios.put(
      `${API_BASE}/assignments/${assignmentId}/self-assessment`,
      data,
      {
        headers: authHeader()
      }
    );
  },
  clarifyAssignment: (assignmentId, data) => {
    return axios.put(`${API_BASE}/assignments/${assignmentId}/clarify`, data, {
      headers: authHeader()
    });
  },
  getMySummary: (periodId, userName) => {
    return axios.get(`${API_BASE}/my-summary`, {
      params: { periodId, userName },
      headers: authHeader()
    });
  },
  commitMyKpi: periodId => {
    return axios.post(
      `${API_BASE}/periods/${periodId}/commit`,
      {},
      { headers: authHeader() }
    );
  },
  reorderMyAssignments: (periodId, assignmentIds) => {
    return axios.put(
      `${API_BASE}/my-assignments/reorder`,
      { periodId, assignmentIds },
      { headers: authHeader() }
    );
  },

  // 4. การอนุมัติตัวชี้วัดรายบุคคลตามสายบังคับบัญชา (Subordinate Sequential Approval)
  getSubordinatesApproval: periodId => {
    return axios.get(`${API_BASE}/periods/${periodId}/subordinates-approval`, {
      headers: authHeader()
    });
  },
  approveAssignment: assignmentId => {
    return axios.put(
      `${API_BASE}/assignments/${assignmentId}/approve`,
      {},
      { headers: authHeader() }
    );
  },
  rejectAssignment: (assignmentId, data) => {
    return axios.put(`${API_BASE}/assignments/${assignmentId}/reject`, data, {
      headers: authHeader()
    });
  },
  approveAllSubordinateAssignments: (periodId, targetUserName) => {
    return axios.put(
      `${API_BASE}/periods/${periodId}/subordinates/${targetUserName}/approve-all`,
      {},
      { headers: authHeader() }
    );
  }
};
